import { Link, NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useEffect, useState } from "react";
import { LayoutDashboard, Users, Kanban, Building2, LogOut, Menu, X, Settings, Search, Send } from "lucide-react";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";

const NAV = [
  { to: "/imobiliaria", label: "Visão geral", icon: LayoutDashboard, testid: "nav-agency-dashboard", end: true },
  { to: "/imobiliaria/distribuir", label: "Distribuir leads", icon: Send, testid: "nav-agency-distribute" },
  { to: "/imobiliaria/leads", label: "Todos os leads", icon: Kanban, testid: "nav-agency-leads" },
  { to: "/imobiliaria/agenciamentos", label: "Agenciamentos", icon: Building2, testid: "nav-agency-agenciamentos" },
  { to: "/imobiliaria/imoveis", label: "Buscar imóveis", icon: Search, testid: "nav-agency-property-search" },
  { to: "/imobiliaria/corretores", label: "Corretores", icon: Users, testid: "nav-agency-realtors" },
  { to: "/imobiliaria/config", label: "Configurações", icon: Settings, testid: "nav-agency-settings" },
];

export default function AgencyLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [agency, setAgency] = useState(null);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    // Master admin com agency_id também tem acesso (modo demo).
    const canAccess = user?.role === "agency_admin" ||
      (user?.role === "admin" && user?.agency_id);
    if (!canAccess) { navigate("/"); return; }
    api.get("/agency/me").then((r) => setAgency(r.data)).catch(() => {});
  }, [user, navigate]);

  return (
    <div className="min-h-screen flex bg-[#0A1937]">
      <header className="lg:hidden fixed top-0 inset-x-0 z-40 bg-[#071129]/90 backdrop-blur border-b border-white/10 px-4 h-14 flex items-center justify-between">
        <button data-testid="mobile-menu-toggle" onClick={() => setOpen(!open)} className="p-2 -ml-2 text-white">
          {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
        </button>
        <Link to="/imobiliaria" className="font-heading font-bold text-lg text-white">estatebroker<span className="text-[#3B9EFF]">.AI</span></Link>
        <div className="w-9 h-9 rounded-full bg-[#3B9EFF] text-white flex items-center justify-center font-semibold text-sm">
          {user?.name?.[0]?.toUpperCase() || "G"}
        </div>
      </header>

      <aside className={`fixed lg:sticky top-0 left-0 z-30 h-screen w-72 bg-[#071129] border-r border-white/10 transform transition-transform lg:transform-none ${open ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'} flex flex-col`}>
        <div className="px-6 pt-6 pb-4 border-b border-white/10">
          <Link to="/imobiliaria" onClick={() => setOpen(false)} className="block">
            <div className="font-heading text-xl font-extrabold text-white tracking-tight">
              estatebroker<span className="text-[#3B9EFF]">.AI</span>
            </div>
            <div className="text-xs text-white/50 mt-1">Painel da imobiliária</div>
          </Link>
          {agency && (
            <div className="mt-4 p-3 bg-[#3B9EFF]/10 border border-[#3B9EFF]/25 rounded-lg">
              <div className="flex items-center gap-2 text-[10px] uppercase tracking-wider text-[#3B9EFF] font-bold">
                <Building2 className="w-3 h-3" /> Imobiliária
              </div>
              <div className="text-white font-semibold text-sm mt-1 truncate" data-testid="agency-name-display">{agency.name}</div>
              <div className="text-white/50 text-xs">{agency.city}, {agency.state}</div>
              <div className="text-white/70 text-xs mt-1"><b>{agency.realtor_count}</b> corretor{agency.realtor_count !== 1 ? "es" : ""}</div>
            </div>
          )}
        </div>

        <nav className="flex-1 p-4 space-y-1">
          {NAV.map(({ to, label, icon: Icon, testid, end }) => (
            <NavLink key={to} to={to} end={end} onClick={() => setOpen(false)} data-testid={testid}
              className={({ isActive }) => `flex items-center gap-3 px-4 py-3 rounded-lg font-medium text-sm transition ${isActive ? 'bg-[#3B9EFF] text-white' : 'text-white/70 hover:bg-white/6'}`}>
              <Icon className="w-5 h-5" /> {label}
            </NavLink>
          ))}
        </nav>

        <div className="p-4 border-t border-white/10">
          <div className="flex items-center gap-3 px-2 py-2 mb-2">
            <div className="w-10 h-10 rounded-full bg-[#3B9EFF] text-white flex items-center justify-center font-bold">
              {user?.name?.[0]?.toUpperCase() || "G"}
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-semibold truncate text-white">{user?.name}</div>
              <div className="text-xs text-white/50">{user?.role === "admin" ? "Master (modo demo)" : "Gestora"}</div>
            </div>
          </div>
          {user?.role === "admin" && (
            <Link to="/admin" data-testid="agency-back-to-admin"
              className="flex items-center gap-2 text-xs text-white/60 hover:text-white px-2 py-1.5 mb-1">
              ← Voltar pro painel admin
            </Link>
          )}
          <Button variant="ghost" onClick={logout} data-testid="logout-btn" className="w-full justify-start text-white/70 hover:text-red-400 hover:bg-white/5">
            <LogOut className="w-4 h-4 mr-2" /> Sair
          </Button>
        </div>
      </aside>

      {open && <div className="lg:hidden fixed inset-0 z-20 bg-black/50" onClick={() => setOpen(false)} />}

      <main className="flex-1 min-w-0 pt-14 lg:pt-0 bg-[#0A1937] text-white">
        <Outlet />
      </main>
    </div>
  );
}
